import { useState, useEffect } from "react";
import axios from "axios";
import { X, Download, FileText, RefreshCw, ClipboardList, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SwimLevelBadge from "./SwimLevelBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DailyChangesModal({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchDailyChanges();
  }, [selectedDate]);

  const fetchDailyChanges = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/daily-changes?date=${selectedDate}`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching daily changes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    window.open(`${BACKEND_URL}/api/daily-changes/pdf?date=${selectedDate}`, '_blank');
  };

  const handleDownloadCSV = () => {
    window.open(`${BACKEND_URL}/api/daily-changes/csv?date=${selectedDate}`, '_blank');
  };

  const formatTime = (isoString) => {
    if (!isoString) return "-";
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    } catch {
      return "-";
    }
  };

  const formatDisplayDate = (dateStr) => {
    try {
      const date = new Date(dateStr + 'T12:00:00');
      return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-slate-800 rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col border border-slate-700"
        onClick={(e) => e.stopPropagation()}
        data-testid="daily-changes-modal"
      >
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-500/20 rounded-full">
                <ClipboardList className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Daily Color Changes</h2>
                <p className="text-sm text-slate-400">Track swim color changes by date</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              data-testid="close-daily-changes-modal"
            >
              <X className="h-5 w-5 text-slate-400" />
            </button>
          </div>

          {/* Date picker and actions */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-400" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                data-testid="date-picker"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPDF}
                disabled={loading || !data?.changes?.length}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                data-testid="download-pdf-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadCSV}
                disabled={loading || !data?.changes?.length}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                data-testid="download-csv-btn"
              >
                <FileText className="h-4 w-4 mr-2" />
                CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 text-purple-400 animate-spin" />
              <span className="ml-3 text-slate-400">Loading changes...</span>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-white mb-3">{formatDisplayDate(selectedDate)}</h3>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">Total Changes:</span>
                    <Badge variant="outline" className="bg-purple-500/20 text-purple-300 border-purple-500/50">
                      {data?.total_changes || 0}
                    </Badge>
                  </div>
                  {data?.by_color && Object.entries(data.by_color).map(([color, count]) => (
                    <div key={color} className="flex items-center gap-2">
                      <span className="text-slate-400">→ {color}:</span>
                      <Badge variant="outline" className="text-white">
                        {count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Changes list */}
              {data?.changes?.length > 0 ? (
                <div className="space-y-2">
                  <div className="hidden sm:grid grid-cols-6 gap-2 text-xs text-slate-400 uppercase tracking-wide px-3 py-2">
                    <span className="col-span-2">Camper</span>
                    <span>Division</span>
                    <span>Old Color</span>
                    <span>New Color</span>
                    <span className="text-right">Time</span>
                  </div>
                  
                  {data.changes.map((change, index) => (
                    <div 
                      key={index}
                      className="bg-slate-900/30 rounded-lg p-3 hover:bg-slate-900/50 transition-colors"
                      data-testid={`change-row-${index}`}
                    >
                      {/* Mobile layout */}
                      <div className="sm:hidden space-y-2">
                        <div className="flex justify-between items-start">
                          <span className="font-medium text-white">{change.camper_name}</span>
                          <span className="text-xs text-slate-500">{formatTime(change.changed_at)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-slate-400">{change.division || "-"}</span>
                          <span className="text-slate-600">•</span>
                          <span className="text-slate-400">{change.bunk || "-"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <SwimLevelBadge color={change.old_color} />
                          <span className="text-slate-500">→</span>
                          <SwimLevelBadge color={change.new_color} />
                        </div>
                      </div>

                      {/* Desktop layout */}
                      <div className="hidden sm:grid grid-cols-6 gap-2 items-center">
                        <span className="col-span-2 font-medium text-white">{change.camper_name}</span>
                        <span className="text-sm text-slate-400">{change.division || "-"}</span>
                        <SwimLevelBadge color={change.old_color} />
                        <SwimLevelBadge color={change.new_color} />
                        <span className="text-sm text-slate-400 text-right">{formatTime(change.changed_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <ClipboardList className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No color changes recorded for this date.</p>
                  <p className="text-sm text-slate-500 mt-2">Changes are logged when swim colors are updated via sync or manual edits.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
